// Bilingual content for the entire site.
// Spanish is the default. English copy is hand-written, not machine translated.

export const I18N = {
  es: {
    nav: {
      home: "Inicio",
      about: "Sobre mí",
      help: "Cómo te ayudo",
      sessions: "Sesiones",
      contact: "Contacto",
    },
    header: {
      cta: "Agenda una sesión",
      langLabel: "Idioma",
    },
    seo: {
      title: "VeronikaconK | Transición profesional hacia trabajo remoto e híbrido",
      description:
        "Acompañamiento profesional para personas que quieren transformar su experiencia presencial en nuevas oportunidades remotas o híbridas.",
    },
    hero: {
      eyebrow: "Transición profesional",
      tagline: "Claridad hoy. Libertad mañana.",
      headline: {
        l1: "No empiezas",
        l2: "de cero.",
        l3: "Empiezas desde",
        l4: "lo que",
        l5: "ya sabes.",
      },
      body:
        "Cambiar de carrera o pasar de un trabajo presencial a uno remoto no significa dejar atrás todo lo que has construido. Te ayudo a entender cómo funciona el mundo laboral actual, reconocer el valor de tu experiencia y dar tu siguiente paso con más claridad.",
      ctaPrimary: "Agenda una sesión de exploración",
      ctaSecondary: "Conoce mi historia",
      languages: "Sesiones 1:1 disponibles en español e inglés.",
      quote: "Tu experiencia no es un punto de partida. Es una base.",
    },
    signals: {
      eyebrow: "Cuando sabes que necesitas un cambio",
      headline: {
        l1: "Quieres un cambio,",
        l2: "pero no sabes",
        l3: "cómo empezar.",
      },
      lead: "A veces no falta capacidad. Falta dirección.",
      body:
        "Puede que sientas que tu experiencia ya no encaja, que el trabajo remoto es un mundo completamente nuevo o que necesitas comenzar desde cero. Pero muchas de las habilidades que ya desarrollaste pueden acompañarte hacia una nueva etapa profesional.",
      blocks: [
        {
          title: "No tienes claro tu siguiente paso",
          text: "Sabes que quieres algo diferente, pero todavía no encuentras el camino.",
        },
        {
          title: "Sientes que debes empezar de cero",
          text: "Tu experiencia tiene valor. Solo necesita una nueva perspectiva.",
        },
        {
          title: "El mundo laboral cambió",
          text: "Las formas de trabajar, comunicarte y encontrar oportunidades evolucionaron.",
        },
        {
          title: "No confías del todo en tus habilidades",
          text: "Reconocer tus fortalezas es el primer paso para poder utilizarlas.",
        },
      ],
    },
    about: {
      eyebrow: "Sobre mí",
      headline: "No tienes que empezar de cero para cambiar tu vida profesional.",
      paragraphs: [
        "Mi trabajo en hostelería, atención al público y servicio al cliente nunca fue el problema. Me gustaba ayudar a las personas, resolver situaciones y sentir que podía hacer una diferencia en su día.",
        "Lo que empezó a pesar fue todo lo que ocurría antes de llegar a trabajar.",
        "Pasaba alrededor de tres horas y media al día trasladándome. Cuando la crisis en Venezuela hizo que cosas tan básicas como conseguir gasolina o repuestos para un coche se volvieran inciertas, entendí algo importante: mi capacidad de generar ingresos dependía demasiado de poder llegar físicamente a un lugar.",
        "Quería una alternativa, pero no sabía por dónde empezar.",
        "No tenía experiencia en trabajo remoto. No conocía las herramientas. No sabía quién podría contratarme ni cómo explicar que mi experiencia presencial también tenía valor en otro entorno.",
        "Mi primera oportunidad llegó por la recomendación de una amiga en Twitter. Al principio pensé que podía ser una estafa, pero decidí intentarlo.",
        "Ese paso cambió mi forma de ver lo que ya sabía hacer.",
        "Descubrí que la resolución de problemas, la proactividad, la comunicación con clientes y la capacidad de encontrar alternativas no pertenecían únicamente a un trabajo presencial. Eran habilidades reales, transferibles y valiosas.",
        "Con el tiempo, pasé a trabajar de forma 100% remota como asistente virtual para una empresa de Estados Unidos y llegué a gestionar operaciones para cuatro sedes.",
        "Mi trabajo incluyó nóminas, contratos, coordinación de equipos, impuestos, recursos, relación con otras compañías y apoyo en solicitudes de créditos y préstamos.",
        "También he fortalecido mi forma de trabajar a través de formación en trabajo en equipo, Agile, administración y recursos humanos. Desde el año 2000 formo parte de la Asociación Scout de Venezuela, una experiencia que me ha enseñado planificación, organización, comunicación y cómo acompañar a personas de distintas edades y realidades.",
        "Hoy ayudo a personas que quieren cambiar de carrera, profesión o modalidad de trabajo, especialmente a quienes vienen de entornos presenciales y sienten que el mundo remoto no fue hecho para ellas.",
        "No estoy aquí para prometerte un empleo ni para decirte que el cambio será fácil o inmediato.",
        "Estoy aquí para ayudarte a ver lo que ya tienes.",
        "A reconocer las habilidades que has construido, entender cómo funciona el entorno laboral actual, traducir tu experiencia a nuevas oportunidades y tomar decisiones con más claridad.",
        "No voy a hacer el proceso por ti. Voy a ayudarte a construir las herramientas y la confianza para que puedas hacerlo por ti mismo.",
      ],
      closing:
        "No necesitas saber exactamente qué quieres hacer para comenzar. Solo necesitas estar dispuesto a mirar tu experiencia con otros ojos.",
      cta: "Descubre lo que ya tienes para ofrecer",
      caption: "Veronika, fundadora de VeronikaconK.",
      beforeLabel: "Antes",
      beforeText: "Atención al público y servicio al cliente.",
      afterLabel: "Hoy",
      afterText: "Gestión administrativa 100% remota.",
    },
    help: {
      eyebrow: "Cómo puedo ayudarte",
      headline: {
        l1: "Tu experiencia",
        l2: "puede abrir",
        l3: "nuevas puertas.",
      },
      lead: "Trabajaremos desde lo que ya sabes, no desde todo lo que crees que te falta.",
      steps: [
        {
          n: "01",
          title: "Encuentra claridad",
          text: "Ordena tus ideas y entiende qué tipo de cambio profesional estás buscando.",
        },
        {
          n: "02",
          title: "Reconoce lo que ya tienes",
          text: "Identifica las habilidades, experiencias y conocimientos que siguen teniendo valor en un entorno remoto o híbrido.",
        },
        {
          n: "03",
          title: "Define un siguiente paso realista",
          text: "Convierte la confusión en acciones concretas que puedas comenzar a explorar.",
        },
      ],
    },
    sessions: {
      eyebrow: "Sesión de Claridad Profesional",
      headline: {
        l1: "Empecemos por",
        l2: "entender dónde",
        l3: "estás.",
      },
      subtitle:
        "Un espacio práctico para entender dónde estás y definir cómo puedes avanzar.",
      detailsTitle: "Qué incluye",
      details: [
        "Sesión 1:1 pagada de 45 minutos",
        "Disponible en español o inglés",
        "Revisión de tu CV, metas, experiencia, habilidades y dirección profesional",
        "Después de la sesión recibirás por correo recomendaciones prácticas para mejorar tu CV y herramientas sugeridas para explorar",
      ],
      disclaimer:
        "Esta sesión no garantiza empleo ni resultados en un plazo específico. Su objetivo es ayudarte a construir claridad, dirección y herramientas para avanzar por tu cuenta.",
      cta: "Reservar Sesión de Claridad",
      othersTitle: "Otras formas en las que puedo ayudarte",
      othersLead:
        "Tres servicios pensados para distintos momentos del proceso. Todos en formato 1:1.",
      idealLabel: "Ideal para",
      includesLabel: "Al terminar, recibirás un correo personalizado con:",
      services: [
        {
          key: "clarity",
          name: "Sesión de Claridad Profesional",
          duration: "30 minutos",
          forYou:
            "Para ti si sabes que quieres un cambio, pero todavía no tienes claro hacia dónde ir ni cómo empezar.",
          description:
            "Revisaremos tu experiencia, tu CV, tus metas y las habilidades que ya tienes para identificar qué posibilidades podrían encajar contigo en un entorno remoto o híbrido.",
          includes: [
            "Posibles caminos profesionales para explorar",
            "Herramientas que podrías comenzar a aprender",
            "Recomendaciones prácticas para fortalecer tu CV",
            "Próximos pasos claros y realistas",
          ],
          idealFor:
            "Personas que se sienten perdidas, quieren cambiar de carrera o creen que tienen que empezar desde cero.",
          price: "USD $50",
        },
        {
          key: "interview",
          name: "Preparación para Entrevista",
          duration: "45 minutos",
          forYou:
            "Para ti si ya tienes una entrevista concreta y quieres llegar con más claridad, estructura y confianza.",
          description:
            "Antes de la sesión, envíame la oferta laboral y tu CV. Durante la llamada trabajaremos cómo presentar tu experiencia, responder preguntas difíciles y comunicar tus habilidades transferibles de forma más convincente.",
          includes: [
            "Puntos clave para destacar en la entrevista",
            "Recomendaciones para mejorar tus respuestas",
            "Ideas para conectar tu experiencia con el puesto",
            "Aspectos concretos a preparar antes de la entrevista",
          ],
          idealFor:
            "Personas que quieren practicar para una entrevista real, especialmente si vienen de otro sector o están entrando al mundo remoto.",
          price: "USD $65",
        },
        {
          key: "strategy",
          name: "Sesión de Estrategia Profesional",
          duration: "60 minutos",
          forYou:
            "Para ti si ya sabes que quieres avanzar hacia un trabajo remoto, híbrido o una nueva área, pero necesitas una estrategia más clara.",
          description:
            "Revisaremos tus roles objetivo, cómo posicionar tu experiencia, qué ajustar en tu CV y LinkedIn, qué palabras clave considerar y cómo organizar una búsqueda más enfocada.",
          includes: [
            "Una dirección profesional más definida",
            "Recomendaciones para adaptar tu CV a roles específicos",
            "Ideas para fortalecer tu perfil de LinkedIn",
            "Palabras clave relevantes para explorar",
            "Un plan de acción inicial para los próximos 30 días",
          ],
          idealFor:
            "Personas listas para empezar una transición profesional activa y crear una estrategia propia.",
          price: "USD $85",
        },
      ],
      importantTitle: "Nota importante",
      importantNote:
        "Estas sesiones no garantizan empleo, entrevistas ni resultados dentro de un plazo específico. Su propósito es ayudarte a reconocer lo que ya sabes, tomar decisiones con más claridad y construir herramientas para avanzar por tu cuenta.",
      reserve: "Reservar",
    },
    form: {
      eyebrow: "Contacto",
      title: "Descubre qué sesión puede ayudarte más",
      subtitle:
        "Cuéntame un poco sobre ti y te orientaré hacia la sesión más adecuada para tu momento profesional.",
      fields: {
        name: "Nombre completo",
        namePh: "Tu nombre",
        email: "Correo electrónico",
        emailPh: "tu@correo.com",
        country: "País o zona horaria",
        countryPh: "Ej. España (CET), México, Argentina (GMT-3)…",
        language: "Idioma preferido",
        langEs: "Español",
        langEn: "English",
        situation: "Situación actual",
        situationPh: "Selecciona una opción",
        situations: [
          "Estoy trabajando y quiero cambiar",
          "Estoy buscando trabajo",
          "Quiero pasar de presencial a remoto o híbrido",
          "Quiero cambiar de carrera o área profesional",
          "Otro",
        ],
        helpType: "¿Qué tipo de ayuda buscas?",
        helpTypePh: "Selecciona una opción",
        helpTypes: [
          "Necesito claridad sobre mi próximo paso",
          "Quiero revisar mi CV y habilidades",
          "Necesito practicar para una entrevista",
          "Quiero ayuda con LinkedIn, aplicaciones o palabras clave",
          "No estoy seguro todavía",
        ],
        message: "Cuéntame brevemente qué cambio estás buscando",
        messagePh: "En unas líneas, dime dónde estás y qué buscas…",
        linkedin: "Link a LinkedIn (opcional)",
        linkedinPh: "https://www.linkedin.com/in/...",
        consent:
          "Entiendo que esta orientación no garantiza empleo ni resultados específicos.",
      },
      submit: "Quiero descubrir mi siguiente paso",
      validation: {
        required: "Este campo es obligatorio",
        email: "Ingresa un email válido",
        consent: "Necesitamos tu confirmación para continuar",
      },
      success:
        "Gracias por compartir tu información. Se abrirá un correo para que puedas enviar tu solicitud directamente. Te responderé con orientación sobre cuál sesión puede ser más útil para ti.",
      mailtoSubject: "Solicitud de orientación profesional",
      bodyLabels: {
        name: "Nombre completo",
        email: "Correo electrónico",
        country: "País / zona horaria",
        language: "Idioma preferido",
        situation: "Situación actual",
        helpType: "Tipo de ayuda buscada",
        message: "Sobre el cambio que busca",
        linkedin: "LinkedIn",
        consent: "Consentimiento",
        consentYes: "Aceptado",
      },
    },
    forWho: {
      eyebrow: "Para quién es",
      headline: {
        l1: "Este espacio",
        l2: "es para ti",
        l3: "si…",
      },
      items: [
        "Quieres cambiar de carrera, profesión o forma de trabajar.",
        "Estás explorando una transición hacia un modelo remoto o híbrido.",
        "Vienes de un trabajo presencial y no sabes cómo adaptar tu experiencia.",
        "Sientes que tus habilidades no encajan en el mercado laboral actual.",
        "Quieres dejar de sentirte perdido y empezar a encontrar una dirección más clara.",
        "Quieres entender mejor cómo aprovechar lo que ya sabes.",
      ],
    },
    finalCta: {
      headline: {
        l1: "Tu próximo paso",
        l2: "no tiene que comenzar",
        l3: "desde cero.",
      },
      body:
        "No necesitas tener todas las respuestas hoy. Solo necesitas empezar a reconocer todo lo que ya tienes para ofrecer.",
      cta: "Descubre lo que ya tienes para ofrecer",
      languages: "Sesiones disponibles en español e inglés.",
    },
    footer: {
      tagline:
        "Transición profesional para un mundo de trabajo más flexible.",
      brandLine: "Claridad hoy. Libertad mañana.",
      languages: "Sesiones disponibles en español e inglés.",
      contactLabel: "Contacto",
      rights: "Todos los derechos reservados",
    },
    quiz: {
      eyebrow: "Quiz · 2 minutos",
      headlineL1: "Reconoce tus",
      headlineL2: "habilidades",
      headlineL3: "transferibles.",
      body:
        "Responde 6 preguntas honestas y verás cinco roles remotos que podrían conversar con tu experiencia actual. Es un punto de partida para reflexionar, no un veredicto.",
      meta1: "Toma 2 minutos",
      meta2: "Resultado inmediato",
      meta3: "Disponible en español",
      introBadge: "Tu mapa remoto personalizado",
      introHeadL1: "6 preguntas.",
      introHeadL2: "Una nueva dirección.",
      introBody:
        "Voy a hacerte 6 preguntas sobre tu experiencia, energía y forma de trabajar. Al final verás tus 5 roles remotos más compatibles.",
      start: "Empezar el diagnóstico",
      prev: "Anterior",
      resultEyebrow: "Tu resultado",
      resultHeadL1: "Estos son tus",
      resultHeadL2: "5 roles remotos",
      resultHeadL3: "más compatibles.",
      afterEyebrow: "¿Quieres conversarlo?",
      afterHead:
        "Si te resuenan estos roles, podemos mirar juntos cómo encajan con tu experiencia real.",
      afterBody:
        "Esto es solo un punto de partida. Si quieres explorarlo a fondo, podemos hacerlo en una sesión de exploración.",
      restart: "Repetir quiz",
      bookSession: "Agenda una sesión de exploración",
    },
  },
  en: {
    nav: {
      home: "Home",
      about: "About",
      help: "How I Help",
      sessions: "Sessions",
      contact: "Contact",
    },
    header: {
      cta: "Book a session",
      langLabel: "Language",
    },
    seo: {
      title: "VeronikaconK | Career transition into remote and hybrid work",
      description:
        "Career transition guidance for people who want to transform their in-person experience into new remote or hybrid opportunities.",
    },
    hero: {
      eyebrow: "Career transition",
      tagline: "Clarity today. Freedom tomorrow.",
      headline: {
        l1: "You don’t start",
        l2: "from zero.",
        l3: "You start from",
        l4: "what you",
        l5: "already know.",
      },
      body:
        "Changing careers or moving from an in-person job to a remote one does not mean leaving behind what you have built. I help you understand how today’s work environment functions, recognize the value of your experience, and take your next step with more clarity.",
      ctaPrimary: "Book an exploration session",
      ctaSecondary: "Read my story",
      languages: "1:1 sessions available in English and Spanish.",
      quote: "Your experience is not a starting point. It is a foundation.",
    },
    signals: {
      eyebrow: "When you know you need a change",
      headline: {
        l1: "You want a change,",
        l2: "but you don’t know",
        l3: "where to begin.",
      },
      lead: "Sometimes it is not about capacity. It is about direction.",
      body:
        "You might feel that your experience no longer fits, that remote work is a whole new world, or that you need to start over from scratch. The truth is, many of the skills you have already developed can carry you into your next professional chapter.",
      blocks: [
        {
          title: "You don’t know what your next step is",
          text: "You know you want something different — you just haven’t found the path yet.",
        },
        {
          title: "You feel you have to start from zero",
          text: "Your experience has value. It just needs a new perspective.",
        },
        {
          title: "The world of work has changed",
          text: "How we work, communicate, and find opportunities has evolved.",
        },
        {
          title: "You don’t fully trust your skills yet",
          text: "Recognizing your strengths is the first step to using them.",
        },
      ],
    },
    about: {
      eyebrow: "About me",
      headline:
        "You do not have to start from zero to change your professional life.",
      paragraphs: [
        "My work in hospitality, customer-facing roles, and customer service was never the problem. I enjoyed helping people, solving issues, and knowing that I could make a meaningful difference in someone’s day.",
        "What became difficult was everything that happened before I even got to work.",
        "I spent around three and a half hours each day commuting. When Venezuela’s crisis made basics like fuel and car parts difficult to access, I realized something important: my ability to earn an income depended too heavily on being able to physically reach one place.",
        "I wanted another option, but I had no idea where to begin.",
        "I had no remote-work experience. I did not know the tools. I did not know who would hire me or how to explain that my in-person experience could still be valuable in a different environment.",
        "My first opportunity came through a friend’s recommendation on Twitter. At first, I thought it might be a scam. Still, I decided to try.",
        "That one step changed the way I saw the skills I already had.",
        "I learned that problem-solving, initiative, client communication, and the ability to find practical alternatives were not limited to an in-person job. They were real, transferable, and valuable skills.",
        "Over time, I moved into a fully remote virtual assistant role with a U.S. company and eventually managed operations across four locations.",
        "My responsibilities included payroll, contracts, team coordination, taxes, resources, relationships with partner companies, and support with credit and loan requests.",
        "My approach is also shaped by training in teamwork, Agile, administration, and human resources. Since 2000, I have been part of the Venezuelan Scout Association, an experience that has strengthened my skills in planning, organization, communication, and supporting people of different ages and backgrounds.",
        "Today, I help people who want to change careers, professions, or work models, especially those coming from in-person roles who feel that remote work was built for someone else.",
        "I am not here to promise you a job or tell you that change will happen overnight.",
        "I am here to help you see what you already have.",
        "To recognize the skills you have built, understand today’s work environment, translate your experience into new opportunities, and make your next decisions with more clarity.",
        "I will not do the work for you. I will help you build the tools and confidence to do it for yourself.",
      ],
      closing:
        "You do not need to know exactly what you want to do before you begin. You only need to be open to seeing your experience from a different perspective.",
      cta: "Discover what you already have to offer",
      caption: "Veronika, founder of VeronikaconK.",
      beforeLabel: "Before",
      beforeText: "Customer-facing hospitality and service.",
      afterLabel: "Today",
      afterText: "100% remote administrative management.",
    },
    help: {
      eyebrow: "How I can help you",
      headline: {
        l1: "Your experience",
        l2: "can open",
        l3: "new doors.",
      },
      lead: "We work from what you already know — not from everything you believe you lack.",
      steps: [
        {
          n: "01",
          title: "Find clarity",
          text: "Organize your thoughts and understand what kind of professional change you’re looking for.",
        },
        {
          n: "02",
          title: "Recognize what you already have",
          text: "Identify the skills, experiences, and knowledge that still carry value in a remote or hybrid environment.",
        },
        {
          n: "03",
          title: "Define a realistic next step",
          text: "Turn confusion into concrete actions you can begin to explore.",
        },
      ],
    },
    sessions: {
      eyebrow: "Career Clarity Session",
      headline: {
        l1: "Let’s start by",
        l2: "understanding",
        l3: "where you are.",
      },
      subtitle:
        "A practical space to understand where you are and define how you can move forward.",
      detailsTitle: "What’s included",
      details: [
        "Paid 45-minute one-on-one session",
        "Available in English or Spanish",
        "Review of your CV, goals, experience, skills, and professional direction",
        "After the session you’ll receive a personalized email with suggested tools and practical recommendations to improve your CV",
      ],
      disclaimer:
        "This session does not guarantee employment or results within a specific timeframe. Its purpose is to help you build clarity, direction, and tools to move forward on your own.",
      cta: "Book the Clarity Session",
      othersTitle: "Other ways I can help",
      othersLead:
        "Three services for different moments of the journey. All one-on-one.",
      idealLabel: "Ideal for",
      includesLabel: "Afterwards, you’ll receive a personalized email with:",
      services: [
        {
          key: "clarity",
          name: "Career Clarity Session",
          duration: "30 minutes",
          forYou:
            "For you if you know you want a change, but you’re not yet clear on where to go or how to begin.",
          description:
            "We’ll review your experience, your CV, your goals, and the skills you already have to identify which possibilities could fit you in a remote or hybrid environment.",
          includes: [
            "Possible professional paths to explore",
            "Tools you could start learning",
            "Practical recommendations to strengthen your CV",
            "Clear and realistic next steps",
          ],
          idealFor:
            "People who feel lost, want to change careers, or believe they have to start from zero.",
          price: "USD $50",
        },
        {
          key: "interview",
          name: "Interview Preparation",
          duration: "45 minutes",
          forYou:
            "For you if you already have a specific interview and want to arrive with more clarity, structure, and confidence.",
          description:
            "Before the session, send me the job offer and your CV. During the call we’ll work on how to present your experience, answer difficult questions, and communicate your transferable skills more convincingly.",
          includes: [
            "Key points to highlight in the interview",
            "Recommendations to improve your answers",
            "Ideas to connect your experience with the role",
            "Specific aspects to prepare before the interview",
          ],
          idealFor:
            "People who want to practice for a real interview — especially if they come from another industry or are entering the remote world.",
          price: "USD $65",
        },
        {
          key: "strategy",
          name: "Career Strategy Session",
          duration: "60 minutes",
          forYou:
            "For you if you already know you want to move toward remote, hybrid, or a new area — but you need a clearer strategy.",
          description:
            "We’ll review your target roles, how to position your experience, what to adjust in your CV and LinkedIn, which keywords to consider, and how to organize a more focused search.",
          includes: [
            "A more defined professional direction",
            "Recommendations to tailor your CV to specific roles",
            "Ideas to strengthen your LinkedIn profile",
            "Relevant keywords to explore",
            "An initial action plan for the next 30 days",
          ],
          idealFor:
            "People ready to begin an active career transition and build their own strategy.",
          price: "USD $85",
        },
      ],
      importantTitle: "Important note",
      importantNote:
        "These sessions do not guarantee employment, interviews, or results within a specific timeframe. Their purpose is to help you recognize what you already know, make decisions with more clarity, and build tools to move forward on your own.",
      reserve: "Book",
    },
    form: {
      eyebrow: "Contact",
      title: "Discover which session may help you most",
      subtitle:
        "Tell me a little about where you are, and I will help guide you toward the session that best fits your current professional stage.",
      fields: {
        name: "Full name",
        namePh: "Your name",
        email: "Email",
        emailPh: "you@email.com",
        country: "Country or time zone",
        countryPh: "e.g. Spain (CET), Mexico, Argentina (GMT-3)…",
        language: "Preferred language",
        langEs: "Español",
        langEn: "English",
        situation: "Current situation",
        situationPh: "Select an option",
        situations: [
          "I’m working and want to change",
          "I’m currently job-hunting",
          "I want to move from in-person to remote or hybrid",
          "I want to change careers or industries",
          "Other",
        ],
        helpType: "What kind of help are you looking for?",
        helpTypePh: "Select an option",
        helpTypes: [
          "I need clarity on my next step",
          "I want to review my CV and skills",
          "I need to practice for an interview",
          "I want help with LinkedIn, applications, or keywords",
          "I’m not sure yet",
        ],
        message: "Tell me briefly what change you’re looking for",
        messagePh: "In a few lines, share where you are and what you’re looking for…",
        linkedin: "LinkedIn link (optional)",
        linkedinPh: "https://www.linkedin.com/in/...",
        consent:
          "I understand this guidance does not guarantee employment or specific outcomes.",
      },
      submit: "Help me discover my next step",
      validation: {
        required: "This field is required",
        email: "Enter a valid email",
        consent: "We need your confirmation to continue",
      },
      success:
        "Thank you for sharing your information. An email draft will open so you can send your request directly. I will reply with guidance on which session may be most helpful for you.",
      mailtoSubject: "Career guidance request",
      bodyLabels: {
        name: "Full name",
        email: "Email",
        country: "Country / time zone",
        language: "Preferred language",
        situation: "Current situation",
        helpType: "Type of help requested",
        message: "About the change being sought",
        linkedin: "LinkedIn",
        consent: "Consent",
        consentYes: "Accepted",
      },
    },
    forWho: {
      eyebrow: "Who this is for",
      headline: {
        l1: "This space",
        l2: "is for you",
        l3: "if…",
      },
      items: [
        "You want to change careers, professions, or how you work.",
        "You’re exploring a transition into a remote or hybrid model.",
        "You come from an in-person job and don’t know how to translate your experience.",
        "You feel your skills don’t fit today’s job market.",
        "You want to stop feeling lost and start finding a clearer direction.",
        "You want to better understand how to use what you already know.",
      ],
    },
    finalCta: {
      headline: {
        l1: "Your next step",
        l2: "does not have to",
        l3: "begin from zero.",
      },
      body:
        "You do not need to have every answer today. You only need to begin recognizing everything you already have to offer.",
      cta: "Discover what you already have to offer",
      languages: "Sessions available in English and Spanish.",
    },
    footer: {
      tagline:
        "Career transition guidance for a more flexible world of work.",
      brandLine: "Clarity today. Freedom tomorrow.",
      languages: "Sessions available in English and Spanish.",
      contactLabel: "Contact",
      rights: "All rights reserved",
    },
    quiz: {
      eyebrow: "Quiz · 2 minutes",
      headlineL1: "Recognize your",
      headlineL2: "transferable",
      headlineL3: "skills.",
      body:
        "Answer 6 honest questions and see five remote roles that may resonate with your current experience. It’s a starting point for reflection — not a verdict.",
      meta1: "Takes 2 minutes",
      meta2: "Immediate result",
      meta3: "Available in English",
      introBadge: "Your personalized remote map",
      introHeadL1: "6 questions.",
      introHeadL2: "A new direction.",
      introBody:
        "I’ll ask you 6 questions about your experience, energy, and way of working. At the end you’ll see your 5 most compatible remote roles.",
      start: "Start the quiz",
      prev: "Previous",
      resultEyebrow: "Your result",
      resultHeadL1: "These are your",
      resultHeadL2: "5 most compatible",
      resultHeadL3: "remote roles.",
      afterEyebrow: "Want to talk it through?",
      afterHead:
        "If these roles resonate, we can look together at how they fit your real experience.",
      afterBody:
        "This is just a starting point. If you want to explore it in depth, we can do so in an exploration session.",
      restart: "Restart quiz",
      bookSession: "Book an exploration session",
    },
  },
};
